/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import $ from 'jquery';
import { handleScroll } from './utils';
const SLOT_ITEM_TOUCH_DRAG_DURATION = 600;

class DndSlotItemHandler {
	/**
	 * DndSlotItemHandler hides most of the drag and drop handling used by the glass DndManager
	 * It handles drop of data from the metadata tree to a Slot Item (or Slot Heading)
	 * It also handles Drag&Drop of slot to slot data.
	 *
	 * @param {Object} 	options
	 * @param {DOMNode} options.domNode - DOM node to attach drag and drop to
	 * @param {string} 	options.className - class name
	 * @param {Function} options.slotDataItemHandler - the slotDataItemHandler.
	 * @param {Boolean}	options.addAfter - true if this drop is after a slot
	 * @param {Object} 	options.dndManager - the drag&drop manager.
	 * @param {Function} [options.onDragEnter] - oprional function to be executed in addition to activateDropTarget when entering this target
	 * @param {Function} [options.onDragLeave] - oprional function to be executed in addition to activateDropTarget when exiting this target
	 * @param {Boolean} [options.isVisOverlay] - if set, this target is a visOverlay....rules are determined in VisDnDManager.
	 */
	constructor(options) {
		this.domNode = options.domNode;
		this.className = options.className;

		this.slotDataItemHandler = options.slotDataItemHandler;

		this.addAfter = options.addAfter;
		this.dndManager = options.dndManager;
		this.onDragEnter = options.onDragEnter;	//custom drag enter handling (in addition to activateDropTarget)
		this.onDragLeave = options.onDragLeave;	//custom drag enter handling (in addition to deactivateDropTarget)
		this.isVisOverlay = options.isVisOverlay;
		this.timer = null;

		// Register slot as a drop target
		if (this.dndManager) {
			this._setupDnD();
		}
	}

	_setupDnD() {
		const onDrop = this.slotDataItemHandler &&
			this.slotDataItemHandler.onDrop &&
			this.slotDataItemHandler.onDrop.bind(this.slotDataItemHandler);

		//NOTE: registering a new dropTarget on a node derigisters the old one.
		this.dndManager.registerDropTarget({
			el: this.domNode,
			type: 'slot.item',
			info: (domNode, event) => ({
				//position info
				dragInfo: {
					position: {
						x: event.clientX,
						y: event.clientY
					}
				},
				node: domNode,

				//drag&drop handlers
				onDragEnter: this.activateDropTarget.bind(this),
				onDragLeave: this.deactivateDropTarget.bind(this),
				onDrop: onDrop,

				//slot data variables.
				contentId: this.slotDataItemHandler.getContentId(),
				slot: this.slotDataItemHandler.getSlotAPI(),
				indexInSlot: this.slotDataItemHandler.indexInSlot,
				addAfter: this.addAfter,
				isVisOverlay: this.isVisOverlay
			})
		});
	}

	destroy() {
		this.dndManager.deregisterDropTarget(this.domNode);
	}

	updateDropTarget(slotDataItemHandler) {
		if (this.dndManager) {
			this.slotDataItemHandler = slotDataItemHandler;
			this._setupDnD();
		}
	}

	activateDropTarget() {
		$(this.domNode).toggleClass(this.className, true);
		if (this.onDragEnter) {
			this.onDragEnter();
		}
	}

	deactivateDropTarget() {
		$(this.domNode).toggleClass(this.className, false);
		if (this.onDragLeave) {
			this.onDragLeave();
		}
	}

	onDragMove(dragObject, dropNode, event) {
		const slotEditorNode = $(dropNode).closest('.slotEditor').get(0);
		if(slotEditorNode) {
			handleScroll(event, slotEditorNode, () => this.dndManager && this.dndManager.reassessContainer(slotEditorNode));
		}
		this.activateDropTarget();
	}

	/**
	 * @param dndManager - the dndManager to initialize the drag within
	 * @param cbGetSlotDataItemHandler - a callback function which should be called when starting any drag to get the up-to-date item data handler.
	 */
	initializeDndSource(dndManager, cbGetSlotDataItemHandler, iconsFeature, startDragCallBack) {
		// Separate mouse and touch event handlers for defect 241719
		$(this.domNode).on('mousedown', {}, (event) => {
			if(startDragCallBack) {
				startDragCallBack();
			}
			this._startDrag({
				dndManager: dndManager,
				event: event,
				cbGetSlotDataItemHandler: cbGetSlotDataItemHandler,
				iconsFeature
			});
			return true;
		});

		$(this.domNode).on('touchstart', {}, (event) => {
			if(startDragCallBack) {
				startDragCallBack();
			}
			this._touchStart({
				dndManager: dndManager,
				event: event,
				cbGetSlotDataItemHandler: cbGetSlotDataItemHandler,
				iconsFeature
			});
			return true;
		});

		$(this.domNode).on('touchend touchcancel', {}, () => this._touchEnd());
	}

	// defect 241719
	// after touchstart, wait short time to start drag process if still touch,
	// so that it can reduce change of ambiguous event with scrolling of the slot editor
	_touchStart(options) {
		this.timer = setTimeout(function() {
			this._touchStartDrag(options);
		}.bind(this), SLOT_ITEM_TOUCH_DRAG_DURATION);
	}

	_touchEnd() {
		if (this.timer) {
			clearTimeout(this.timer);
		}
		this.timer = null;
	}

	_touchStartDrag(options) {
		if (this.timer) {
			this._startDrag(options);
		}
	}

	_startDrag(options) {
		let $avatar = $('<div style="position:absolute; z-index:10000;"/>');

		const $draggedNode = $(this.domNode).clone().addClass('draggedNode');
		const { width } = this.domNode.getBoundingClientRect();
		$draggedNode.css({
			width,
			cursor: 'default'
		});
		if(options.iconsFeature) {
			const disableIcon = options.iconsFeature.getIcon('common-nodrop');
			if(disableIcon) {
				const $nodropSign = $(
					`<div class="nodrop is-hidden">
						<svg class="svgIcon">
							<use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#${disableIcon.id}">
							</use>
						</svg>
					</div>`
				);
				$draggedNode.find('.bi-common-field-item__nameLine__draggable').remove();
				$draggedNode.find('.bi-common-field-item__nameLine').prepend($nodropSign);
			}
		}
		$avatar.append($draggedNode);

		const sourceSlotDataItemHandler = options.cbGetSlotDataItemHandler();
		options.dndManager.startDrag({
			event: options.event,
			type: 'SLOT_ITEM',
			data: {
				source: {
					slot: sourceSlotDataItemHandler.getSlotAPI(),
					indexInSlot: sourceSlotDataItemHandler.indexInSlot
				}
			},
			callerCallbacks: {
				onMove: this._onMove.bind(this)
			},
			avatar: $avatar,
			moveXThreshold: 20,
			moveYThreshold: 20
		});
	}

	_onMove(event, payload = {}) {
		const { dragObject } = payload;
		if(dragObject) {
			const { avatar } = dragObject;
			if (avatar) {
				const $avatar = $(avatar);
				const $nodrop = $avatar.find('.nodrop');
				if($nodrop.length) {
					const { dropTargetNode } = payload;
					if(!dropTargetNode) {
						// no payload.dropTargetNode means currenly you cannot drop here, so we show `not allowed drop sign`
						$nodrop.toggleClass('is-hidden', false);
					} else {
						// else payload.dropTargetNode exists,
						// if the drop target is not `.slotEditor` (which is used for drag auto scroll ),
						// then means currenly you can REALLY drop the slot item here, so we does not show `not allowed drop sign`
						const isDropTargetNotSlotEditor = !(dropTargetNode.classList && dropTargetNode.classList.contains('slotEditor'));
						$nodrop.toggleClass('is-hidden', isDropTargetNotSlotEditor);
					}
				}
			}
		}
	}
}

export default DndSlotItemHandler;



// WEBPACK FOOTER //
// ./src/js/DndSlotItemHandler.js